import { FC } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGuard } from '@/guards/AuthGuard';
import { Permission } from '@/models/user';

// Import pages
import Login from '../pages/login';
import Dashboard from '../pages/dashboard/index';
import EnvDashboardPage from '../pages/dashboard/envDashboard';
import Customers from '../pages/customers/index';
import CustomersDetails from '../pages/customers/details';
import Tickets from '../pages/tickets/index';
import TicketsDetails from '../pages/tickets/details';
import Vouchers from '../pages/vouchers/index';
import OrderTickets from '../pages/orderTickets/index';
import UserManagement from '../pages/userManagement/index';
import ProductConfiguration from '../pages/productConfiguration/index';
import ProductConfigurationDetails from '../pages/productConfiguration/details/index';
import FilterTagsPage from '../pages/filterTags/index';
import TagsPage from '../pages/tags/index';
import Rulesets from '../pages/rulesets/index';
import RulesetsDetails from '../pages/rulesets/details';
import RulesetsNeu from '../pages/rulesets/neu';
import RulesetsImportieren from '../pages/rulesets/importieren';
import SpecialTariffs from '../pages/specialTariffs/index';
import SpecialTariffsDetails from '../pages/specialTariffs/details';
import SpecialTariffsNeu from '../pages/specialTariffs/neu';
import Hospitality from '../pages/hospitality/index';
import HospitalityNeu from '../pages/hospitality/neu';
import HospitalityBearbeiten from '../pages/hospitality/bearbeiten';
import StandardShops from '../pages/standardShops/index';
import B2B from '../pages/b2b/index';
import FAQ from '../pages/faq/index';
import Billings from '../pages/billings/index';
import MailConfiguration from '../pages/mailConfiguration/index';
import TicketTemplates from '../pages/ticketTemplates/index';
import TextCustomizations from '../pages/textCustomizations/index';
import Einstellungen from '../pages/settings';
import VoucherTemplates from '../pages/gutscheinvorlagen/index';
import VoucherTemplateEditor from '../pages/gutscheinvorlagen/bearbeiten';
import WidgetEditor from '../pages/widgetEditor';
import NotFound from '../pages/404';
import UnAuthorized from '../pages/UnAuthorized';

import { PageWithLayout } from '@/models/page';

interface RouteConfig {
    path: string;
    component: PageWithLayout;
    isProtected?: boolean;
    permissions?: Permission[];
    layout?: 'sidebar' | 'split';
}

const routes: RouteConfig[] = [
    { path: '/login', component: Login, isProtected: false, layout: 'split' },
    {
        path: '/',
        component: Dashboard,
    },
    {
        path: '/dashboard',
        component: Dashboard,
    },
    {
        path: '/dashboard/:dashboardEnv',
        component: EnvDashboardPage,
    },
    {
        path: '/kunden',
        component: Customers,
    },
    {
        path: '/kunden/details',
        component: CustomersDetails,
    },
    {
        path: '/tickets',
        component: Tickets,
    },
    {
        path: '/tickets/details',
        component: TicketsDetails,
    },
    {
        path: '/vouchers',
        component: Vouchers,
    },
    {
        path: '/ticket-bestellen',
        component: OrderTickets,
    },
    {
        path: '/benutzerverwaltung',
        component: UserManagement,
    },
    {
        path: '/produktkonfiguration',
        component: ProductConfiguration,
    },
    {
        path: '/produktkonfiguration/details',
        component: ProductConfigurationDetails,
    },
    {
        path: '/produktkonfiguration/filtertags',
        component: FilterTagsPage,
    },
    {
        path: '/produktkonfiguration/tags',
        component: TagsPage,
    },
    {
        path: '/regelwerke',
        component: Rulesets,
    },
    {
        path: '/regelwerke/details',
        component: RulesetsDetails,
    },
    {
        path: '/regelwerke/neu',
        component: RulesetsNeu,
    },
    {
        path: '/regelwerke/importieren',
        component: RulesetsImportieren,
    },
    {
        path: '/spezialtarife',
        component: SpecialTariffs,
    },
    {
        path: '/spezialtarife/details',
        component: SpecialTariffsDetails,
    },
    {
        path: '/spezialtarife/neu',
        component: SpecialTariffsNeu,
    },
    {
        path: '/hospitality',
        component: Hospitality,
    },
    {
        path: '/hospitality/neu',
        component: HospitalityNeu,
    },
    {
        path: '/hospitality/bearbeiten',
        component: HospitalityBearbeiten,
    },
    {
        path: '/standardshops',
        component: StandardShops,
    },
    {
        path: '/b2b',
        component: B2B,
    },
    {
        path: '/faq',
        component: FAQ,
    },
    {
        path: '/abrechnungen',
        component: Billings,
    },
    {
        path: '/mail-konfiguration',
        component: MailConfiguration,
    },
    {
        path: '/ticketvorlagen',
        component: TicketTemplates,
    },
    {
        path: '/text-anpassungen-ticket',
        component: TextCustomizations,
    },
    {
        path: '/einstellungen',
        component: Einstellungen,
    },
    {
        path: '/gutscheinvorlagen',
        component: VoucherTemplates,
    },
    {
        path: '/gutscheinvorlagen/bearbeiten',
        component: VoucherTemplateEditor,
    },
    {
        path: '/widget-editor',
        component: WidgetEditor,
    },
    { path: '/404', component: NotFound },
    { path: '/unauthorized', component: UnAuthorized },
];

const RouteWrapper: FC<{ route: RouteConfig }> = ({ route }) => {
    const Component = route.component;

    const getLayout = Component.getLayout ?? ((page) => page);

    const render = () => getLayout(<Component />);

    if (Component.isProtected) {
        return <AuthGuard Component={Component}>{render()}</AuthGuard>;
    }

    return render();
};

export const AppRoutes: FC = () => {
    return (
        <Routes>
            {routes.map((route) => (
                <Route
                    key={route.path}
                    path={route.path}
                    element={<RouteWrapper route={route} />}
                />
            ))}
            <Route path='*' element={<Navigate to='/404' replace />} />
        </Routes>
    );
};
